export type SegmentStatus = "not_started" | "in_progress" | "approved" | "requires_review" | "new" | "removed";
export interface Segment {
  id: string;
  /** Stable UUID persisted on the server — survives source re-sync */
  sectionId?: string;
  structuralPath?: string;
  sourceHash?: string;
  clauseId: string;
  sourceText: string;
  status: SegmentStatus;
  mergedText?: string;
  existingTranslation?: string;
  /** When true (approved via Save & Next), sync will not auto-overwrite */
  locked?: boolean;
  /** Alignment index for Lexical injection */
  injectionIndex?: number;
}
export interface GlossaryEntry {
  id: string;
  english: string;
  french: string;
  avoid?: string;
  notes?: string;
}
export interface Draft {
  sourceId: "A" | "B" | "C";
  text: string;
}
export type DocumentStatus = "translating" | "in_review" | "completed";
export interface Document {
  id: string;
  title: string;
  createdAt: string;
  sourceLanguage: string;
  targetLanguage: string;
  segments: Segment[];
  status: DocumentStatus;
  isActive: boolean;
  enBody?: string;
  frBody?: string | null;
  templateId?: number;
  frTemplateId?: number | null;
  locales?: string[];
}
export const mockSegments: Segment[] = [{
  id: "seg-1",
  clauseId: "1.1 Definitions",
  sourceText: 'In this Agreement, "Confidential Information" means all non-public, proprietary information disclosed by one party to the other. It includes, but is not limited to, pricing, business plans, and customer lists. The receiving party shall not disclose such information to any third party.',
  status: "approved",
  mergedText: "Dans le présent Contrat, les \"Informations Confidentielles\" désignent toutes les informations non publiques et exclusives divulguées par une partie à l'autre. Elles incluent, sans s'y limiter, la tarification, les plans d'affaires et les listes de clients. La partie réceptrice ne doit pas divulguer ces informations à un tiers."
}, {
  id: "seg-2",
  clauseId: "2.1 Term and Termination",
  sourceText: "This Agreement shall commence on the Effective Date and shall continue in full force and effect for a period of two (2) years. Either party may terminate this Agreement upon thirty (30) days prior written notice to the other party. Upon termination, all Confidential Information must be returned or destroyed.",
  status: "in_progress",
  mergedText: "Le présent Contrat prendra effet à la Date d'Entrée en Vigueur et restera pleinement en vigueur pour une période de deux (2) ans."
}, {
  id: "seg-3",
  clauseId: "3.1 Governing Law",
  sourceText: "This Agreement shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law principles. Any legal action or proceeding arising under this Agreement will be brought exclusively in the federal or state courts located in New York County.",
  status: "not_started"
}, {
  id: "seg-4",
  clauseId: "4.1 Indemnification",
  sourceText: "The Service Provider agrees to indemnify, defend, and hold harmless the Client from and against any and all claims, damages, liabilities, costs, and expenses arising out of any breach of this Agreement. This indemnification obligation shall survive the termination of this Agreement.",
  status: "not_started"
}, {
  id: "seg-5",
  clauseId: "5.1 Limitation of Liability",
  sourceText: "In no event shall either party be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with this Agreement. The total liability of either party shall not exceed the total fees paid under this Agreement in the twelve (12) months preceding the claim.",
  status: "not_started"
}, {
  id: "seg-6",
  clauseId: "6.1 Notices",
  sourceText: "All notices required or permitted under this Agreement shall be in writing and shall be deemed delivered when delivered in person or deposited in the United States mail, postage prepaid. Notices shall be sent to the addresses set forth on the signature page of this Agreement.",
  status: "not_started"
}, {
  id: "seg-7",
  clauseId: "7.1 Severability",
  sourceText: "If any provision of this Agreement is held to be invalid, illegal, or unenforceable, the validity, legality, and enforceability of the remaining provisions shall not in any way be affected or impaired thereby. The parties shall negotiate in good faith to replace the invalid provision with a valid one that reflects the original intent.",
  status: "not_started"
}, {
  id: "seg-8",
  clauseId: "8.1 Entire Agreement",
  sourceText: "This Agreement constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior and contemporaneous agreements, understandings, and negotiations. No modification or amendment to this Agreement shall be binding unless in writing and signed by both parties.",
  status: "not_started"
}];
export const mockGlossary: GlossaryEntry[] = [{
  id: "g1",
  english: "pricing",
  french: "tarification",
  avoid: "prix",
  notes: "Always use tarification for SaaS pricing models."
}, {
  id: "g2",
  english: "offices",
  french: "locaux",
  avoid: "bureaux",
  notes: "Refers to physical real estate."
}, {
  id: "g3",
  english: "Effective Date",
  french: "Date d'Entrée en Vigueur",
  notes: "Capitalize as shown."
}, {
  id: "g4",
  english: "Confidential Information",
  french: "Informations Confidentielles"
}, {
  id: "g5",
  english: "breach",
  french: "violation",
  avoid: "rupture"
}, {
  id: "g6",
  english: "indemnify",
  french: "indemniser"
}];
export const mockDocuments: Document[] = [{
  id: "doc-1",
  title: "Master Services Agreement - Project Phoenix",
  createdAt: "2023-10-24T10:00:00Z",
  sourceLanguage: "EN",
  targetLanguage: "FR",
  segments: mockSegments,
  status: "in_review"
}, {
  id: "doc-2",
  title: "Office Lease Agreement - 425 Park Ave",
  createdAt: "2023-10-20T14:30:00Z",
  sourceLanguage: "EN",
  targetLanguage: "FR",
  status: "completed",
  segments: [{
    id: "seg-2-1",
    clauseId: "1. Premises",
    sourceText: "Landlord hereby leases to Tenant, and Tenant hereby leases from Landlord, the premises known as Suite 1400 on the 14th floor of the building located at 425 Park Avenue, New York, NY.",
    status: "approved",
    mergedText: "Le Propriétaire loue par la présente au Locataire, et le Locataire prend à bail du Propriétaire, les locaux connus sous le nom de Suite 1400 au 14ème étage de l'immeuble situé au 425 Park Avenue, New York, NY."
  }, {
    id: "seg-2-2",
    clauseId: "2. Term",
    sourceText: "The term of this Lease shall be for a period of ten (10) years, commencing on the Commencement Date and ending on the Expiration Date, unless sooner terminated pursuant to the terms hereof.",
    status: "approved",
    mergedText: "La durée de ce Bail sera d'une période de dix (10) ans, commençant à la Date de Commencement et se terminant à la Date d'Expiration, à moins qu'il ne soit résilié plus tôt conformément aux termes des présentes."
  }, {
    id: "seg-2-3",
    clauseId: "3. Base Rent",
    sourceText: "Tenant shall pay to Landlord base rent in the amount of $120.00 per rentable square foot per annum, payable in equal monthly installments in advance on the first day of each calendar month.",
    status: "approved",
    mergedText: "Le Locataire paiera au Propriétaire un loyer de base d'un montant de 120,00 $ par pied carré louable par an, payable par versements mensuels égaux d'avance le premier jour de chaque mois civil."
  }]
}, {
  id: "doc-3",
  title: "Non-Disclosure Agreement - Acme Corp",
  createdAt: "2023-10-25T09:15:00Z",
  sourceLanguage: "EN",
  targetLanguage: "FR",
  status: "translating",
  segments: [{
    id: "seg-3-1",
    clauseId: "1. Purpose",
    sourceText: "The parties intend to engage in discussions regarding a potential business relationship. In connection with these discussions, it may be necessary for either party to disclose Confidential Information to the other.",
    status: "not_started"
  }, {
    id: "seg-3-2",
    clauseId: "2. Definition",
    sourceText: '"Confidential Information" means any information, technical data, or know-how, including, but not limited to, that which relates to research, product plans, products, services, customers, markets, software, developments, inventions, processes, designs, drawings, engineering, hardware configuration information, marketing or finances of the disclosing party.',
    status: "not_started"
  }, {
    id: "seg-3-3",
    clauseId: "3. Non-Use and Non-Disclosure",
    sourceText: "The receiving party agrees not to use any Confidential Information of the disclosing party for any purpose except to evaluate and engage in discussions concerning a potential business relationship between the parties.",
    status: "not_started"
  }]
}];
export const mockDrafts: Record<string, Draft[]> = {
  "seg-2": [{
    sourceId: "A",
    text: "Le présent Contrat prendra effet à la Date d'Entrée en Vigueur et restera pleinement en vigueur pour une période de deux (2) ans. L'une ou l'autre des parties peut résilier le présent Contrat moyennant un préavis écrit de trente (30) jours à l'autre partie. À la résiliation, toutes les Informations Confidentielles doivent être retournées ou détruites."
  }, {
    sourceId: "B",
    text: "Cet Accord commencera à la Date d'Entrée en Vigueur et continuera d'avoir plein effet pendant une durée de deux (2) ans. Chaque partie peut mettre fin à cet Accord sur notification écrite préalable de trente (30) jours. Dès la résiliation, toute Information Confidentielle devra être restituée ou détruite."
  }, {
    sourceId: "C",
    text: "Le présent Contrat entrera en vigueur à la Date d'Entrée en Vigueur et demeurera en vigueur pendant une période de deux (2) ans. L'une des parties peut résilier ce Contrat en donnant un préavis écrit de trente (30) jours. En cas de résiliation, toutes les Informations Confidentielles devront être rendues ou détruites."
  }],
  "seg-3": [{
    sourceId: "A",
    text: "Le présent Contrat sera régi et interprété conformément aux lois de l'État de New York, sans égard à ses principes de conflit de lois. Toute action en justice ou procédure découlant du présent Contrat sera portée exclusivement devant les tribunaux fédéraux ou étatiques situés dans le comté de New York."
  }, {
    sourceId: "B",
    text: "Cet Accord sera régi par et interprété selon les lois de l'État de New York, sans tenir compte de ses règles de conflit de lois. Toute action légale ou procédure naissant de cet Accord sera intentée exclusivement devant les cours fédérales ou d'État situées dans le comté de New York."
  }, {
    sourceId: "C",
    text: "Ce Contrat est régi et interprété selon le droit de l'État de New York, sans considération des principes de conflits de lois. Toute poursuite ou procédure légale découlant de ce Contrat sera soumise exclusivement à la juridiction des tribunaux fédéraux ou d'État du comté de New York."
  }]
};

// Helper to generate drafts on the fly for segments that don't have them pre-mocked
export const generateMockDraftsForSegment = (sourceText: string): Draft[] => {
  return [{
    sourceId: "A",
    text: `[Draft A] Traduction générée pour: ${sourceText.substring(0, 50)}...`
  }, {
    sourceId: "B",
    text: `[Draft B] Traduction alternative pour: ${sourceText.substring(0, 50)}...`
  }, {
    sourceId: "C",
    text: `[Draft C] Troisième option pour: ${sourceText.substring(0, 50)}...`
  }];
};